import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import Auth from "../auth";
import { hasAnyRole } from '../../utils/permissions';

const SSEContext = createContext(null);

// Synthetic event type the provider dispatches to listeners after the SSE
// stream (re)connects or recovers from a silent drop. Page components
// subscribe to this to run a reconcile/backfill — SSE has no native backfill,
// so any messages missed while the socket was down must be re-fetched here.
export const SSE_RECONNECTED = "sse_reconnected";

// If no traffic (event OR heartbeat comment) arrives within this window we
// treat the stream as silently dropped. The server sends periodic heartbeats,
// so a gap this long means the native EventSource may be mid-reconnect without
// the server re-emitting `connected`. We force a backfill dispatch on the next
// byte and also drive it from a watchdog.
const HEARTBEAT_GAP_MS = 45000;

export function SSEProvider({ children, active }) {
  const listenersRef = useRef(new Map()); // Map<eventType, Set<callback>>
  const eventSourceRef = useRef(null);
  const rejectedRef = useRef(false);
  const connectionIdRef = useRef(null);
  const lastTrafficRef = useRef(Date.now());
  const sawDropRef = useRef(false);
  const [evicted, setEvicted] = useState(false);

  const subscribe = useCallback((eventType, callback) => {
    if (!listenersRef.current.has(eventType)) {
      listenersRef.current.set(eventType, new Set());
    }
    listenersRef.current.get(eventType).add(callback);

    // Return unsubscribe function
    return () => {
      const set = listenersRef.current.get(eventType);
      if (set) {
        set.delete(callback);
        if (set.size === 0) listenersRef.current.delete(eventType);
      }
    };
  }, []);

  // Dispatch a synthetic event to all listeners of `type`. Used both for the
  // reconnect-backfill signal and (indirectly) for any provider-driven event.
  const dispatchSynthetic = useCallback((type, payload) => {
    const callbacks = listenersRef.current.get(type);
    if (callbacks) {
      callbacks.forEach((cb) => cb(payload ?? { type }));
    }
  }, []);

  // Notify the server to remove this connection (works during page unload)
  const sendDisconnect = useCallback(() => {
    const connId = connectionIdRef.current;
    if (!connId) return;
    navigator.sendBeacon(`/events/disconnect?id=${connId}`);
    connectionIdRef.current = null;
    sessionStorage.removeItem("bdr_sse_connection_id");
  }, []);

  // Connects (or reconnects) an EventSource for this tab
  const connectSSE = useCallback(() => {
    // Clean up any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    // Notify server about the previous connection if any
    sendDisconnect();

    if (!active) return;
    if (!Auth.loggedIn()) return;

    const token = Auth.getToken();
    const currentUser = Auth.getProfile();
    const userId = currentUser?.data?._id;
    const permission = currentUser?.data?.permission;

    if (!hasAnyRole(permission, ['admin', 'office'])) return;

    // Don't clear the banner here — only clear it when server confirms connection
    const es = new EventSource(`/events/stream?token=${token}`);
    eventSourceRef.current = es;

    // Server accepted this connection — store connectionId and clear the banner
    es.addEventListener("connected", (event) => {
      try {
        const data = JSON.parse(event.data);
        connectionIdRef.current = data.connectionId;
        // Mirror to sessionStorage so the Apollo authLink can attach the
        // x-bdr-connection-id header on every mutation. sessionStorage is
        // per-tab, so each tab carries its own connectionId.
        if (data.connectionId) {
          sessionStorage.setItem("bdr_sse_connection_id", data.connectionId);
        }
      } catch (e) {
        // no connectionId in payload — still connected
      }
      rejectedRef.current = false;
      setEvicted(false);
      lastTrafficRef.current = Date.now();
      // Reconnect backfill (mandatory): SSE has no native replay, so on every
      // (re)open tell listeners to reconcile. Always fires — covers both the
      // first connect and an explicit server-driven reconnect.
      sawDropRef.current = false;
      dispatchSynthetic(SSE_RECONNECTED, { type: SSE_RECONNECTED });
    });

    // Server rejected this connection (at capacity) — show banner, close to prevent auto-reconnect
    es.addEventListener("rejected", () => {
      console.log("SSE connection rejected (too many tabs) — will retry in 10s");
      es.close();
      eventSourceRef.current = null;
      rejectedRef.current = true;
      setEvicted(true);
    });

    // Server heartbeat, sent as a NAMED event (comment-line heartbeats never
    // reach JS, which starved the watchdog on healthy-quiet streams). Only
    // refreshes the traffic clock. Listener lifetime is tied to this
    // EventSource instance — es.close() tears it down with the connection.
    es.addEventListener("hb", () => {
      lastTrafficRef.current = Date.now();
    });

    es.onmessage = (event) => {
      // Any byte from the server (event or heartbeat) means the stream is live.
      lastTrafficRef.current = Date.now();
      // If the watchdog flagged a silent drop, native EventSource has
      // reconnected without the server re-emitting `connected` — backfill now.
      if (sawDropRef.current) {
        sawDropRef.current = false;
        dispatchSynthetic(SSE_RECONNECTED, { type: SSE_RECONNECTED });
      }
      try {
        const data = JSON.parse(event.data);

        // Skip our own events (except job form updates — always show those
        // since the same user may submit from mobile and review on web).
        //
        // Push 1 build 146: also exempt `job_form_payment_completed`. The
        // Call For Payment workflow has the office user click "Open
        // Payment Portal in New Tab" which loads in the same browser
        // session under the same userId. Without this exemption, the
        // payment-completed SSE event from saveTransaction is filtered
        // out as "our own" and the JobFormReviewModal never refetches —
        // banner stays, approval stays blocked, user has to manually
        // close + reopen. This was sign-off bug #2 caught before push.
        //
        // build 147: third exemption is server-driven via
        // `data.allowSelfDeliver`. The server sets it on `calendar_update`
        // events when the originating user has more than one active SSE
        // connection (e.g. calendar in a separate window), so the other
        // tab can refresh silently. The originator tab is identified by
        // `data.originConnectionId` and suppresses its own snackbar
        // downstream in the navbar handler.
        if (
          data.originUserId &&
          data.originUserId === userId &&
          !data.allowSelfDeliver &&
          data.type !== "job_form_update" &&
          data.type !== "job_form_payment_completed"
        ) return;

        // Notify all listeners for this event type
        const callbacks = listenersRef.current.get(data.type);
        if (callbacks) {
          callbacks.forEach((cb) => cb(data));
        }
      } catch (err) {
        // Ignore parse errors (heartbeats, etc.)
      }
    };

    es.onerror = () => {
      // Only log if not rejected — rejected tabs close intentionally
      if (!rejectedRef.current) {
        console.log("SSE connection error — will auto-reconnect");
        // Native EventSource will silently auto-reconnect; mark a drop so the
        // next inbound byte (or the `connected` re-emit) triggers a backfill.
        sawDropRef.current = true;
      }
    };
  }, [active, sendDisconnect, dispatchSynthetic]);

  useEffect(() => {
    connectSSE();

    // If rejected by server, retry every 10s until a slot opens
    const retryInterval = setInterval(() => {
      if (rejectedRef.current) {
        connectSSE();
      }
    }, 10000);

    // Heartbeat-gap watchdog: if no traffic (event or heartbeat) for longer
    // than HEARTBEAT_GAP_MS, treat the stream as silently dropped and REPAIR
    // it — tear down + reconnect (a half-open socket after sleep/NAT rebind
    // never fires onerror, so native EventSource would stay frozen forever).
    // sawDrop stays set so the backfill floor still holds even if the new
    // connection's `connected` re-emit is missed. Resetting the traffic clock
    // here is the double-connect guard: the reconnect attempt gets a full
    // HEARTBEAT_GAP_MS to hand-shake before the watchdog can trip again
    // (connectSSE itself always closes the previous EventSource first).
    const watchdogInterval = setInterval(() => {
      if (rejectedRef.current) return;
      if (!eventSourceRef.current) return;
      if (Date.now() - lastTrafficRef.current > HEARTBEAT_GAP_MS) {
        sawDropRef.current = true;
        lastTrafficRef.current = Date.now();
        connectSSE();
      }
    }, 15000);

    // Explicitly disconnect on tab close/navigation away — bypasses proxy issues
    const handleUnload = () => sendDisconnect();
    window.addEventListener("beforeunload", handleUnload);

    return () => {
      // Cleanup on unmount (logout, route change, etc.)
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      sendDisconnect();
      clearInterval(retryInterval);
      clearInterval(watchdogInterval);
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [connectSSE, sendDisconnect]);

  return (
    <SSEContext.Provider value={{ subscribe, evicted }}>
      {children}
    </SSEContext.Provider>
  );
}

// Hook to check if this tab's SSE connection was rejected (too many tabs)
export function useSSEEvicted() {
  const context = useContext(SSEContext);
  return context?.evicted ?? false;
}

// Hook to listen for specific event types
export function useSSEListener(eventType, callback) {
  const context = useContext(SSEContext);

  useEffect(() => {
    if (!context) return;
    const unsubscribe = context.subscribe(eventType, callback);
    return unsubscribe;
  }, [context, eventType, callback]);
}

export default SSEContext;
